import Router from "next/router";
import { create } from "zustand";
import { getUserOrganizationPermissions } from "@/services/user";
import {
  getOrganizationProfile,
  getOrganizationCurrentEmployees,
  getOrganizationFormerEmployees,
  getPaymentInfo,
  getOrgPayments,
  getOrgRepayments,
  getOrgApplications,
  getCurrentEmployeesByLicense,
  getPaymentsPPInfo,
  getODHCurrentEmployees,
  getDODDCurrentEmployees,
  getOrganization1099s,
  getNewHireFormSchemaAndData,
  getNewHireFormCenterTypes,
  setEmployeeECEGrantee,
} from "@/services/organization";
import { baseURL } from "@/services/http";

export const useOrgDashboardStore = create((set) => ({
  organizations: [],
  organizationProfile: null,
  selectedOrganization: null,
  enrollmentData: [],
  employmentData: [],
  currentEmployees: null,
  formerEmployees: null,
  orgPayments: [],
  orgRepayments: [],
  org1099s: null,
  orgApplications: null,
  paymentInformation: null,
  orgCurrentEmployeesLoading: false,
  orgFormerEmployeesLoading: false,
  currentEmployeesByLicense: null,
  ppInfo: null,
  orgCurrentEmployeesByLicenseLoading: false,
  employeeTabIndex: 0,
  loading: false,
  applicationsLoading: false,
  organizationLoading: false,
  paymentInfoLoading: false,
  repaymentLoading: false,
  paymentLoading: false,
  ppInfoLoading: false,
  initialOrgLoad: false,
  formDataLoading: false,
  formSchemaLoading: false,
  loading1099s: false,
  error: "",
  successMessage: "",
  newHireOpened: false,
  selectedEmployee: null,
  newHireFormData: null,
  newHireGroupedFields: null,
  newHireFormikMap: null,
  groupedChecklistFields: null,
  newHireFormCenterTypes: [],
  newHireFormSaving: false,
  setSelectedOrganization: async (organization) => {
    const { organizationLoading } = useOrgDashboardStore.getState();
    if (organizationLoading) return;
    if (!organization) {
      set((state) => ({
        ...state,
        selectedOrganization: null,
        organizationProfile: null,
      }));
      return;
    }
    try {
      let organizationProfile = await getOrganizationProfile(organization.id);
      const {
        org_type,
        info,
        employees,
        unverified_employees,
        organization_id_hash,
      } = organizationProfile;
      const enrollmentData = mapEnrollmentData(info);
      const employmentData = mapEmploymentData(employees);
      if (org_type === "qrtp") {
        window.open(
          `${baseURL}organization_profile/index/${organization_id_hash}`,
          "_blank"
        );
      } else {
        if (Router.pathname.includes("/organizationDashboard")) {
          const paths = Router.asPath.split("/");
          const currentOrgId = paths[2]; // Assuming '/organizationDashboard/{organizationid}/someRoute'
          const currentPage = paths[3];
          if (currentOrgId !== organization.id) {
            // Substitute the correct organization ID and redirect
            Router.push(
              `/organizationDashboard/${organization.id}/${currentPage}`
            );
          }
        } else {
          Router.push(`/organizationDashboard/${organization.id}/employees`);
        }
      }
      set((state) => ({
        ...state,
        selectedOrganization: organization,
        organizationProfile,
        enrollmentData,
        employmentData,
        currentEmployees: null,
        formerEmployees: null,
        orgPayments: [],
        orgRepayments: [],
        initialOrgLoad: true,
        paymentInformation: null,
        employeeTabIndex:
          unverified_employees && unverified_employees.length > 0 ? 2 : 0,
        error: "",
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch organization profile at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        organizationLoading: false,
      }));
    }
  },
  refreshSelectedOrganization: async () => {
    const { organizationLoading, selectedOrganization } =
      useOrgDashboardStore.getState();
    if (organizationLoading || !selectedOrganization) return;
    try {
      let organizationProfile = await getOrganizationProfile(
        selectedOrganization.id
      );
      const { info, employees } = organizationProfile;
      const enrollmentData = mapEnrollmentData(info);
      const employmentData = mapEmploymentData(employees);
      set((state) => ({
        ...state,
        organizationProfile,
        enrollmentData,
        employmentData,
        error: "",
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch organization profile at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        organizationLoading: false,
      }));
    }
  },
  setEmployeeTabIndex: (tabIndex) => {
    set((state) => ({
      ...state,
      employeeTabIndex: tabIndex,
    }));
  },
  setNewHireOpened: (isOpened) => {
    set((state) => ({
      ...state,
      newHireOpened: isOpened,
    }));
  },
  setSelectedEmployee: (employee) => {
    set((state) => ({
      ...state,
      selectedEmployee: employee
    }))
  },
  setNewHireFormSaving: (isSaving) => {
    set((state) => ({
      ...state,
      newHireFormSaving: isSaving
    }))
  },
  addPastEmployment: (employeeId, pastEmployment) => {
    const { currentEmployees } = useOrgDashboardStore.getState();
    let employeeIdx =
      currentEmployees.findIndex((e) => e.id === employeeId) + 1;
    const data = [
      ...pastEmployment.map((e, idx) => {
        return { ...e, id: idx + 1 };
      }),
    ];
    const updatedCurrentEmployees = [...currentEmployees];
    data.forEach((d) => {
      updatedCurrentEmployees.splice(employeeIdx, 0, d);
      employeeIdx++;
    });
    set((state) => ({
      ...state,
      currentEmployees: updatedCurrentEmployees,
    }));
  },
  removePastEmployment: () => {
    const { currentEmployees } = useOrgDashboardStore.getState();
    set((state) => ({
      ...state,
      currentEmployees: currentEmployees.filter((e) => e.id > 100),
    }));
  },
  fetchOrganizations: async (clearCache) => {
    const { organizations, loading } = useOrgDashboardStore.getState();
    if (loading) return;
    if (organizations.length > 0 && !clearCache) {
      return organizations;
    }
    set((state) => ({ ...state, loading: true }));
    try {
      let organizations = await getUserOrganizationPermissions();
      organizations = Object.values(organizations).map((o) => ({
        id: o.id,
        name: o.license ? `${o.name} - ${o.license}` : o.name,
      }));
      set((state) => ({
        ...state,
        error: "",
        organizations,
      }));
      return organizations;
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch organization dashboard permissions at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        loading: false,
      }));
    }
  },
  fetchCurrentEmployees: async () => {
    const {
      selectedOrganization,
      organizationLoading,
      orgCurrentEmployeesLoading,
      organizationProfile,
    } = useOrgDashboardStore.getState();
    if (
      organizationLoading ||
      !selectedOrganization ||
      orgCurrentEmployeesLoading
    )
      return;
    try {
      set((state) => ({
        ...state,
        orgCurrentEmployeesLoading: true,
      }));
      let currentEmployees = null;
      if (organizationProfile.org_type === "org") {
        const { employees } = await getOrganizationCurrentEmployees(
          selectedOrganization.id
        );
        currentEmployees = employees;
      } else if (organizationProfile.org_type === "odh") {
        currentEmployees = await getODHCurrentEmployees(
          organizationProfile.info.license_number
        );
        currentEmployees = currentEmployees.map((e) => ({
          ...e,
          hvs_type: (e.hvs && e.hvs.length > 0 && e.hvs[0].name) || null,
          hvs_award_date:
            (e.hvs && e.hvs.length > 0 && e.hvs[0].award_date) || null,
          hvs_expires_date:
            (e.hvs && e.hvs.length > 0 && e.hvs[0].expires_date) || null,
          other_creds_label:
            (e.other_creds && e.other_creds.length > 0 && e.other_creds[0]) ||
            null,
        }));
      } else if (organizationProfile.org_type === "dodd") {
        currentEmployees = await getDODDCurrentEmployees(
          organizationProfile.info.license_number
        );
        currentEmployees = currentEmployees.map((e) => ({
          ...e,
          eiscs_type:
            (e.eiscs && e.eiscs.length > 0 && e.eiscs[0].name) || null,
          eiscs_award_date:
            (e.eiscs && e.eiscs.length > 0 && e.eiscs[0].award_date) || null,
          eiscs_expires_date:
            (e.eiscs && e.eiscs.length > 0 && e.eiscs[0].expires_date) || null,
        }));
      }
      currentEmployees.sort((a, b) => {
        return a.last_name.localeCompare(b.last_name);
      });
      set((state) => ({
        ...state,
        currentEmployees,
        error: "",
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch current employees at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        orgCurrentEmployeesLoading: false,
      }));
    }
  },
  fetchCurrentEmployeesByLicense: async (licenseNumber) => {
    const {
      organizationProfile,
      organizationLoading,
      orgCurrentEmployeesByLicenseLoading,
    } = useOrgDashboardStore.getState();
    if (
      organizationLoading ||
      !organizationProfile ||
      !organizationProfile.info ||
      orgCurrentEmployeesByLicenseLoading
    )
      return;
    try {
      set((state) => ({
        ...state,
        orgCurrentEmployeesByLicenseLoading: true,
      }));
      const currentEmployeesByLicense = await getCurrentEmployeesByLicense(
        organizationProfile.info.license_number
      );
      set((state) => ({
        ...state,
        currentEmployeesByLicense,
        error: "",
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch current employees at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        orgCurrentEmployeesByLicenseLoading: false,
      }));
    }
  },
  fetchFormerEmployees: async () => {
    const {
      selectedOrganization,
      organizationLoading,
      orgFormerEmployeesLoading,
    } = useOrgDashboardStore.getState();
    if (
      organizationLoading ||
      !selectedOrganization ||
      orgFormerEmployeesLoading
    )
      return;
    try {
      set((state) => ({
        ...state,
        orgFormerEmployeesLoading: true,
      }));
      const { employees: formerEmployees } =
        await getOrganizationFormerEmployees(selectedOrganization.id);
      set((state) => ({
        ...state,
        formerEmployees,
        error: "",
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch former employees at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        orgFormerEmployeesLoading: false,
      }));
    }
  },
  setError: (error) => {
    set((state) => ({ ...state, error }));
  },
  setSuccessMessage: (message) => {
    set((state) => ({ ...state, successMessage: message }));
  },
  fetchPaymentInformation: async () => {
    const { selectedOrganization, paymentInfoLoading } =
      useOrgDashboardStore.getState();
    if (paymentInfoLoading || !selectedOrganization) return;
    try {
      set((state) => ({
        ...state,
        paymentInfoLoading: true,
      }));
      const paymentInformation = await getPaymentInfo(selectedOrganization.id);
      set((state) => ({
        ...state,
        paymentInformation: [{ id: 1, ...paymentInformation }],
        error: "",
        paymentInfoLoading: false,
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch payment information at this time. Please try again or contact support.",
      }));
    }
  },
  fetchOrgPayments: async () => {
    const { selectedOrganization, paymentLoading } =
      useOrgDashboardStore.getState();
    if (paymentLoading || !selectedOrganization) return;
    try {
      set((state) => ({
        ...state,
        paymentLoading: true,
      }));
      const orgPayments = await getOrgPayments(selectedOrganization.id);
      set((state) => ({
        ...state,
        orgPayments: orgPayments.map((r, idx) => ({ ...r, id: idx })),
        error: "",
        paymentLoading: false,
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch payment information at this time. Please try again or contact support.",
      }));
    }
  },
  fetchOrgRepayments: async () => {
    const { selectedOrganization, repaymentLoading } =
      useOrgDashboardStore.getState();
    if (repaymentLoading || !selectedOrganization) return;
    try {
      set((state) => ({
        ...state,
        repaymentLoading: true,
      }));
      const orgRepayments = await getOrgRepayments(selectedOrganization.id);
      set((state) => ({
        ...state,
        orgRepayments: orgRepayments.map((r, idx) => ({ ...r, id: idx })),
        error: "",
        repaymentLoading: false,
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch payment information at this time. Please try again or contact support.",
      }));
    }
  },
  fetchOrgPPInfo: async () => {
    const { selectedOrganization, ppInfoLoading } =
      useOrgDashboardStore.getState();
    if (ppInfoLoading || !selectedOrganization) return;
    try {
      set((state) => ({
        ...state,
        ppInfoLoading: true,
      }));
      const { pp_info } = await getPaymentsPPInfo(selectedOrganization.id);
      set((state) => ({
        ...state,
        ppInfo: pp_info,
        error: "",
        ppInfoLoading: false,
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch payment information at this time. Please try again or contact support.",
      }));
    }
  },
  fetchOrgApplications: async () => {
    const { selectedOrganization, applicationsLoading } =
      useOrgDashboardStore.getState();
    if (applicationsLoading || !selectedOrganization) return;
    try {
      set((state) => ({
        ...state,
        applicationsLoading: true,
      }));
      const orgApplications = await getOrgApplications(selectedOrganization.id);
      const { stabilization_apps } = orgApplications;
      if (Object.keys(stabilization_apps).length > 0) {
        // Create a single array of all the stabilization apps and reconciliations (the map returns an array of arrays, then flat combines them into one)
        orgApplications.stabilization_grants = Object.keys(stabilization_apps)
          .map((key) => {
            const {
              apps,
              reconciliation,
              can_reconciliation,
              can_edit_reconciliation,
              funds_remaining,
              label,
              ...rest
            } = stabilization_apps[key];
            let stabilization_grants =
              apps &&
              apps.map((app) => ({
                ...app,
                name: "Grant Application",
                label,
                verification_status: app.status,
              }));
            if (reconciliation.id || can_reconciliation) {
              stabilization_grants = stabilization_grants.concat({
                ...reconciliation,
                id: reconciliation.id || Math.random(),
                name: "Grant Reconciliation",
                label,
                can_edit_reconciliation,
                can_reconciliation,
                funds_remaining,
                verification_status: reconciliation.status,
              });
            }
            return stabilization_grants;
          })
          .flat();

        orgApplications.stabilization_available_projects = Object.values(
          orgApplications.stabilization_available_projects
        );
        // Code to test the reconciliation button
        // orgApplications.stabilization_available_projects = [
        //   {
        //     id: "4",
        //     label: "Phase 4",
        //     start_date: "2023-09-01",
        //     end_date: "2023-12-31",
        //   },
        // ];
      }
      set((state) => ({
        ...state,
        orgApplications,
        error: "",
        applicationsLoading: false,
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch application information at this time. Please try again or contact support.",
      }));
    }
  },
  fetchOrganization1099s: async (licenseNumber) => {
    const { loading1099s } = useOrgDashboardStore.getState();
    if (loading1099s) return;
    set((state) => ({ ...state, loading1099s: true }));
    try {
      let org1099s = await getOrganization1099s(licenseNumber);
      set((state) => ({
        ...state,
        error: "",
        org1099s,
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch organization dashboard 1099s at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        loading1099s: false,
      }));
    }
  },
  fetchNewHireFormSchemaAndData: async (userId, licenseNumber) => {
    const { formSchemaLoading, organizationProfile } = useOrgDashboardStore.getState();
    if (formSchemaLoading) return;
    try {
      set((state) => ({
        ...state,
        formSchemaLoading: true,
        loading: true
      }))
      const newHireFormSchemaAndData = await getNewHireFormSchemaAndData(organizationProfile.info.center_type, userId, licenseNumber);
      const newHireFormSchema = newHireFormSchemaAndData.form_schema;
      const newHireFormData = newHireFormSchemaAndData.form_data;
      const newHireGroupedFields = groupNewHireFieldsBySection(newHireFormSchema);
      const newHireFormikMap = defineNewHireFormikMap(newHireFormSchema, newHireFormData);
      set((state) => ({
        ...state,
        error: "",
        newHireGroupedFields,
        newHireFormikMap,
        newHireFormData,
        formSchemaLoading: false,
        loading: false
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        loading: false,
        error:
          "Unable to fetch new hire form schema at this time. Please try again or contact support."
      }));
    }
  },
  clearNewHireForm: () => {
    set((state) => ({
      ...state,
      newHireGroupedFields: null,
      newHireFormikMap: null,
      newHireFormData: null
    }));
  },
  fetchNewHireFormCenterTypes: async () => {
    try {
      const newHireFormCenterTypes = await getNewHireFormCenterTypes();
      set((state) => ({
        ...state,
        newHireFormCenterTypes
      }))
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch New Hire Form Checklist allowed center types."
      }));
    }
  },
  toggleEmployeeECEGrantee: async (employeeId, isGrantee) => {
    try {
      await setEmployeeECEGrantee(employeeId, isGrantee);
      const { currentEmployees } = useOrgDashboardStore.getState();
      const updatedEmployees = currentEmployees.map((e) => {
        if (e.id === employeeId) {
          return { ...e, ece_staff: isGrantee };
        }
        return e;
      });
      set((state) => ({
        ...state,
        currentEmployees: updatedEmployees,
        error: "",
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to update employee OPP Grantee status at this time. Please try again or contact support.",
      }));
    }
  }

}));

const mapEnrollmentData = (info) => {
  const enrollmentData = [];
  enrollmentData.push({
    ageGroup: "Infants",
    fullTime: info.infant_enrollment,
    partTime: info.infant_enrollment_pt,
    capacity: info.infant_capacity,
    total: Number(info.infant_enrollment) + Number(info.infant_enrollment_pt),
  });
  enrollmentData.push({
    ageGroup: "Toddlers",
    fullTime: info.toddler_enrollment,
    partTime: info.toddler_enrollment_pt,
    capacity: info.toddler_capacity,
    total: Number(info.toddler_enrollment) + Number(info.toddler_enrollment_pt),
  });
  enrollmentData.push({
    ageGroup: "Preschool",
    fullTime: info.pre_school_enrollment,
    partTime: info.pre_school_enrollment_pt,
    capacity: info.pre_school_capacity,
    total:
      Number(info.pre_school_enrollment) +
      Number(info.pre_school_enrollment_pt),
  });
  enrollmentData.push({
    ageGroup: "School Age",
    fullTime: info.school_age_enrollment,
    partTime: info.school_age_enrollment_pt,
    capacity: info.school_age_capacity,
    total:
      Number(info.school_age_enrollment) +
      Number(info.school_age_enrollment_pt),
  });
  enrollmentData.push({
    ageGroup: "Total",
    fullTime:
      Number(info.infant_enrollment) +
      Number(info.toddler_enrollment) +
      Number(info.pre_school_enrollment) +
      Number(info.school_age_enrollment),
    partTime:
      Number(info.infant_enrollment_pt) +
      Number(info.toddler_enrollment_pt) +
      Number(info.pre_school_enrollment_pt) +
      Number(info.school_age_enrollment_pt),
    capacity:
      Number(info.infant_capacity) +
      Number(info.toddler_capacity) +
      Number(info.pre_school_capacity) +
      Number(info.school_age_capacity),
    total:
      Number(info.infant_enrollment) +
      Number(info.toddler_enrollment) +
      Number(info.pre_school_enrollment) +
      Number(info.school_age_enrollment) +
      Number(info.infant_enrollment_pt) +
      Number(info.toddler_enrollment_pt) +
      Number(info.pre_school_enrollment_pt) +
      Number(info.school_age_enrollment_pt),
  });

  return enrollmentData;
};

const mapEmploymentData = (employmentRows) => {
  const employmentData = [];
  let total = 0;
  employmentRows.forEach((row) => {
    employmentData.push({
      title: row.job_title,
      count: row.count,
    });
    total += Number(row.count);
  });
  employmentData.push({
    title: "Total",
    count: total,
  });
  return employmentData;
};

const groupNewHireFieldsBySection = (formSchema) => {
  return formSchema.reduce(function (obj, field) {
    if (!obj[field['section_number']]) { obj[field['section_number']] = []; }
    obj[field['section_number']].push(field);
    return obj;
  }, {});
}

const defineNewHireFormikMap = (newHireFormSchema, newHireFormData) => {
  let formikValues = {};
  newHireFormSchema.forEach((field) => {
    formikValues[field.input_name] = newHireFormData[field.input_name] || false;
    if (field.input_type !== 'checkboxOnly') {
      formikValues[field.input_name + '_input'] = newHireFormData[field.input_name + '_input'] || '';
    }
    if (field.hidden_fields) {
      field.hidden_fields.forEach((field) => {
        formikValues[field.input_name] = newHireFormData[field.input_name] || false;
        if (field.input_type !== 'checkboxOnly') {
          formikValues[field.input_name + '_input'] = newHireFormData[field.input_name + '_input'] || '';
        }
      })
    }
  })
  return formikValues;
}